/**
 * 白板对象模型，与 server/pkg/protocol/whiteboard.go 对应。
 *
 * 坐标一律用页面归一化坐标 0-1，使不同分辨率的客户端呈现一致 ——
 * 老系统用设备坐标，换个屏幕分辨率板书就错位。
 */

export type WBObjKind =
  | 'rect'
  | 'ellipse'
  | 'line'
  | 'roundrect'
  | 'poly'
  | 'pen'
  | 'text'
  | 'image'
  | 'embed'
  /** 矢量图。存的是 SVG 源码，渲染时按当前尺寸重绘，放大不糊 */
  | 'svg'

export type WBOp = 'add' | 'modify' | 'move' | 'copy' | 'delete' | 'appendPt'

export interface WBPoint {
  x: number
  y: number
}

export interface WBStyle {
  lineColor?: string
  fillColor?: string
  lineWidth?: number
  lineStyle?: 'solid' | 'dash' | 'dot'
  opacity?: number
  fontFamily?: string
  fontSize?: number
  fontBold?: boolean
  fontItalic?: boolean
}

export interface WBObject {
  /** 房间内唯一，由服务端分配 */
  id: number
  kind: WBObjKind
  page: number
  ownerId: number
  points?: WBPoint[]
  text?: string
  style: WBStyle
  blobUrl?: string
  /** 对象级锁的持有者 */
  lockBy?: number
  /** 叠放次序 */
  z: number
  time: number
}

export interface WBOpMsg {
  page: number
  op: WBOp
  obj?: WBObject
  objIds?: number[]
  delta?: WBPoint
  points?: WBPoint[]
  objId?: number
  fromId?: number
  time?: number
}

export interface WBPage {
  index: number
  title?: string
  objects: WBObject[]
  /** 页级锁的持有者 */
  lockBy?: number
}

export interface WBSnapshot {
  pages: WBPage[]
  currentPage: number
  nextObjId: number
}

export interface WBPageRef {
  page: number
  title?: string
}

export interface WBLockPage {
  page: number
  lock: boolean
}

export interface WBLockObj {
  objId: number
  lock: boolean
}

export interface WBBlobEnd {
  objId: number
  url: string
}

/** 白板工具。 */
export type WBTool = 'select' | 'pen' | 'line' | 'rect' | 'ellipse' | 'text' | 'eraser'

/** 默认画笔样式。 */
export const DEFAULT_STYLE: WBStyle = {
  lineColor: '#e02020',
  fillColor: '',
  lineWidth: 0.003,
  lineStyle: 'solid',
  opacity: 1,
  fontFamily: 'system-ui, sans-serif',
  fontSize: 0.03,
}

/** 板书常用色板。 */
export const PALETTE = [
  '#1f2933', // 墨黑
  '#e02020', // 朱红
  '#0a84ff', // 蓝
  '#00a870', // 绿
  '#f5a623', // 橙
  '#8b5cf6', // 紫
  '#ffffff', // 白（深色底用）
] as const
